import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const initialData = {
  media_category_id: "",
  title: "",
  slug: "",
  source: "",
  source_url: "",
  news_type: "",
  thumbnail_image: null,
  banner_image: null,
  short_description: "",
  description: "",
  date: new Date().toISOString().split("T")[0],
};

export default function NewsCreate() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [postData, setPostData] = useState(initialData);
  const [previews, setPreviews] = useState({ thumbnail_image: null, banner_image: null });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await ApiRequest("GET", ApiEndpoints.newsCategory.categoryList);
        setCategories(response?.news_categories || []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to fetch media categories.");
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    if (files) {
      const file = files[0];
      if (!file) return;
      setPreviews((previous) => ({ ...previous, [name]: URL.createObjectURL(file) }));
      setPostData((previous) => ({ ...previous, [name]: file }));
      return;
    }
    setPostData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const missingField = Object.entries(postData).find(([, value]) => value === "" || value === null);
    if (missingField) {
      toast.error(`The field "${missingField[0]}" is mandatory.`);
      return;
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => formData.append(key, value));
    try {
      const response = await ApiRequest("POST", ApiEndpoints.news.newsCreate, formData);
      toast.success(response.message);
      navigate("/news");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to create news.");
    }
  };

  const pagename = {
    name: "Media Management",
    user: "Admin",
    innerpage: "Media Management / News / Add News",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="newsAdd" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Title<small>*</small></label></div>
                  <input name="title" value={postData.title} onChange={handleChange} type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Slug<small>*</small></label></div>
                  <input name="slug" value={postData.slug} onChange={handleChange} type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Media Category<small>*</small></label></div>
                  <CustomSelect name="media_category_id" options={categories} onChange={handleChange} placeholder="Select Media Category" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Date<small>*</small></label></div>
                  <input name="date" value={postData.date} onChange={handleChange} type="date" className="form-control" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Source<small>*</small></label></div>
                  <input name="source" value={postData.source} onChange={handleChange} type="text" className="form-control" placeholder="Enter Source Name" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Source URL<small>*</small></label></div>
                  <input name="source_url" value={postData.source_url} onChange={handleChange} type="url" className="form-control" placeholder="https://example.com/article" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>News Type<small>*</small></label></div>
                  <input name="news_type" value={postData.news_type} onChange={handleChange} type="text" className="form-control" placeholder="Enter News Type" />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Short Description<small>*</small></label></div>
                  <textarea name="short_description" value={postData.short_description} onChange={handleChange} className="form-control" rows="4" placeholder="Enter Short Description" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Description<small>*</small></label></div>
                  <DynamicCKEditor data={postData.description} onChange={(event, editor) => setPostData((previous) => ({ ...previous, description: editor.getData() }))} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add"><label>Banner Image (1200 × 580)<small>*</small></label></div>
                  <CustomImageUpload name="banner_image" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.banner_image} />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add"><label>Thumbnail Image (468 × 340)<small>*</small></label></div>
                  <CustomImageUpload name="thumbnail_image" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.thumbnail_image} />
                </div>
              </div>
            </div>
          </div>
        </form>
        <button type="submit" form="newsAdd" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
